import React from "react";
import { useI18nContext } from "@procore/core-react";
import type { CannedMessageType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { AlertContext } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { AlertStore } from "@/stores/alert-store.core";
import { useToastAlertContext } from "@procore/toast-alert";
import { reportError } from "@/utils/logging-utils";
import type { NotifiableError } from "@bugsnag/js";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime/date";
import { stripAndReturnHTMLTokens } from "../../communications/dynamic-tokens/token-utils";
import {
   AssignmentAlertsForm,
   type AssignmentAlertFormValues,
   type AssignmentAlertsFormSubmission,
   type Recipient,
} from "./assignment-alerts-form";

export type SendAssignmentAlertsTearsheetContentProps = {
   projectId: string;
   assignments?: Array<{
      id: string;
      recipientIds: string[];
   }>;
   alertType?: CannedMessageType;
   onClose: () => void;
};

type GetBatchAlertsPayloadParams = {
   values: AssignmentAlertFormValues;
   alertContent: { subject: string; content: string };
   projectId: string;
   assignments: Array<{ id: string; recipientIds: string[] }>;
   allRecipients: Recipient[];
   removedRecipientIds: Set<string>;
   shouldScheduleDelivery: boolean;
};

function getBatchAlertsPayload(params: GetBatchAlertsPayloadParams) {
   const {
      values,
      alertContent,
      projectId,
      assignments,
      allRecipients,
      removedRecipientIds,
      shouldScheduleDelivery,
   } = params;
   const { content: apiReadySubject, dynamicTokens: subjectTokens } = stripAndReturnHTMLTokens(
      alertContent.subject,
      projectId,
   );
   const { content: apiReadyContent, dynamicTokens: contentTokens } = stripAndReturnHTMLTokens(
      alertContent.content,
      projectId,
   );
   const dynamicTokens = subjectTokens.concat(contentTokens);

   const allAssignedResources = assignments.flatMap((assignment) => assignment.recipientIds);
   const allAdditionalRecipientIds = allRecipients
      .filter((recipient: Recipient) => !allAssignedResources.includes(recipient.id))
      .map((recipient: Recipient) => recipient.id);

   const payload: any = {
      project_id: projectId,
      context: shouldScheduleDelivery ? AlertContext.SCHEDULED : AlertContext.OPEN,
      template: {
         content: apiReadyContent,
         subject: apiReadySubject,
         include_signature: false,
         dynamic_tokens: dynamicTokens,
      },
      assignments: assignments.map((assignment) => ({
         id: assignment.id,
         recipient_ids: [
            // Filter out removed recipients from original list
            ...assignment.recipientIds.filter((id) => !removedRecipientIds.has(id)),
            // Add additional recipients (already filtered above)
            ...allAdditionalRecipientIds,
         ],
      })),
   };

   if (shouldScheduleDelivery) {
      payload.context = AlertContext.SCHEDULED;
      payload.scheduled_day = getDetachedDay(values.scheduled_day);
      payload.scheduled_time =
         values.day_night.value === "pm"
            ? values.hour.value + values.minute.value + 12
            : values.hour.value + values.minute.value;
   }

   return payload;
}

/**
 * Create workflow: batches new assignment alerts (send now, schedule, or save draft). Owns only
 * the create API call; the shared `AssignmentAlertsForm` provides the recipients/schedule/content UI.
 */
export const SendAssignmentAlertsTearsheetContent = ({
   projectId,
   assignments = [],
   alertType,
   onClose,
}: SendAssignmentAlertsTearsheetContentProps) => {
   const I18n = useI18nContext();
   const { showToast } = useToastAlertContext();

   async function handleSave({
      context,
      values,
      alertContent,
      allRecipients,
      removedRecipientIds,
   }: AssignmentAlertsFormSubmission) {
      const isDraft = context === AlertContext.DRAFTS;
      try {
         const payload = getBatchAlertsPayload({
            values,
            alertContent,
            projectId,
            assignments,
            allRecipients,
            removedRecipientIds,
            shouldScheduleDelivery: context === AlertContext.SCHEDULED,
         });
         if (isDraft) {
            payload.context = AlertContext.DRAFTS;
         }
         await AlertStore.batchSendAssignmentAlerts(payload).payload;
         showToast.success(
            I18n.t(
               isDraft
                  ? "views.company.workforce_planning.communications.alert_toasts.draft_success"
                  : "views.company.workforce_planning.communications.alert_toasts.sent_success",
            ),
         );
         onClose();
      } catch (error) {
         reportError(
            error as NotifiableError,
            isDraft
               ? "send-assignment-alerts-tearsheet-content__handle-save-draft"
               : "send-assignment-alerts-tearsheet-content__handle-submit",
         );
         showToast.error(
            I18n.t(
               isDraft
                  ? "views.company.workforce_planning.communications.alert_toasts.draft_failure"
                  : "views.company.workforce_planning.communications.alert_toasts.failure",
            ),
         );
      }
   }

   return (
      <AssignmentAlertsForm
         projectId={projectId}
         assignments={assignments}
         alertType={alertType}
         includeProjectDefaults
         onClose={onClose}
         onSave={handleSave}
      />
   );
};
